import React, { useState, useEffect, useRef } from 'react';
import Cookies from 'js-cookie';
import axios from 'axios';
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Checkbox,
  CircularProgress,
  Snackbar,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
} from '@mui/material';
import './VotePrint.css';

// 開會類型選項
const typeOptions = [
  { value: '常會', label: '常會' },
  { value: '第一次臨時會', label: '第一次臨時會' },
  { value: '第二次臨時會', label: '第二次臨時會' },
  { value: '第三次臨時會', label: '第三次臨時會' },
];

// 證件類型選項
const idTypeOptions = [
  { value: '沒正本', label: '沒正本' },
  { value: '戶口名簿', label: '戶口名簿' },
  { value: '戶籍', label: '戶籍' },
  { value: '身分證', label: '身分證' },
  { value: '健保卡', label: '健保卡' },
  { value: '駕照', label: '駕照' }
];

const VotePrint = () => {
  const [selectedYear, setSelectedYear] = useState('');
  const [stockInput, setStockInput] = useState('');
  const [selectedType, setSelectedType] = useState('');
  const [nameInput, setNameInput] = useState('');
  const [selectedIdTypes, setSelectedIdTypes] = useState(idTypeOptions.map(option => option.value));
  const [isIdTypeDropdownOpen, setIsIdTypeDropdownOpen] = useState(false);
  const [years, setYears] = useState([]);
  const idTypeDropdownRef = useRef(null);
  const [searchResults, setSearchResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedRows, setSelectedRows] = useState([]);
  const [showSearchForm, setShowSearchForm] = useState(true);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'error'
  });
  const [printDialogOpen, setPrintDialogOpen] = useState(false);
  const [selectedFormat, setSelectedFormat] = useState('8');
  const [isPrinting, setIsPrinting] = useState(false);

  const printFormatOptions = [
    { value: '1', label: '單頁1筆' },
    { value: '2', label: '單頁2筆' },
    { value: '4', label: '單頁4筆' },
    { value: '6', label: '單頁6筆' },
    { value: '8', label: '單頁8筆' },
  ];

  // 生成年份選項（從當前年份往前推2年）
  useEffect(() => {
    const currentYear = new Date().getFullYear();
    const yearOptions = Array.from({ length: 2 }, (_, i) => currentYear - i - 1911);
    setYears(yearOptions);
    setSelectedYear(currentYear.toString());
    setSelectedType('常會'); // 預設選擇常會
  }, []);

  // 處理點擊外部關閉下拉選單
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (idTypeDropdownRef.current && !idTypeDropdownRef.current.contains(event.target)) {
        setIsIdTypeDropdownOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const handleIdTypeChange = (value) => {
    setSelectedIdTypes(prev => {
      if (prev.includes(value)) {
        return prev.filter(type => type !== value);
      } else {
        return [...prev, value];
      }
    });
  };

  const getSelectedLabels = () => {
    return selectedIdTypes
      .map(value => idTypeOptions.find(option => option.value === value)?.label)
      .join(', ');
  };

  const handleStockInputChange = (e) => {
    const value = e.target.value;
    // Only allow letters, numbers, and Chinese characters
    if (/^[\u4e00-\u9fa5a-zA-Z0-9]*$/.test(value)) {
      setStockInput(value);
    }
  };

  const handleNameInputChange = (e) => {
    const value = e.target.value;
    // Only allow letters, numbers, and Chinese characters
    if (/^[\u4e00-\u9fa5a-zA-Z0-9]*$/.test(value)) {
      setNameInput(value);
    }
  };

  const handleSearch = async () => {
    // Validate input
    if (!stockInput.trim() && !nameInput.trim()) {
      setSnackbar({
        open: true,
        message: '請輸入 股號/股名 或 戶名',
        severity: 'error'
      });
      return;
    }

    try {
      setLoading(true);
      const token = Cookies.get('jwt_token');
      const requestData = {
        year: parseInt(selectedYear) - 1911,  // 轉換為民國年
        certification: selectedIdTypes,
        phyname: nameInput,
        stock: stockInput,
        stock_meeting_type: selectedType
      };

      const response = await axios.post(`${process.env.REACT_APP_API_BASE_URL}/api/voterweb/get`, requestData, {
        headers: {
          Authorization: `Bearer ${token}`
        }
      });

      if (response.status === 200) {
        if (response.data.data && response.data.data.length > 0) {
          setSearchResults(response.data.data);
          setShowSearchForm(false);
        } else {
          setSnackbar({
            open: true,
            message: '查無資料',
            severity: 'warning'
          });
        }
      }
    } catch (error) {
      console.error('Error:', error);
      
      // Handle specific error status codes
      if (error.response) {
        const status = error.response.status;
        let errorMessage = '查詢失敗，請稍後再試';
        
        switch (status) {
          case 401:
            errorMessage = '認證失敗，請重新登入';
            break;
          case 404:
            errorMessage = '伺服器錯誤，請稍後再試';
            break;
          case 500:
            errorMessage = '伺服器錯誤，請稍後再試';
            break;
          default:
            errorMessage = '查詢失敗，請稍後再試';
        }
        
        setSnackbar({
          open: true,
          message: errorMessage,
          severity: 'error'
        });
      } else {
        // Network error or other issues
        setSnackbar({
          open: true,
          message: '網路連線錯誤，請檢查網路連線',
          severity: 'error'
        });
      }
    } finally {
      setLoading(false);
    }
  };

  const handleNewSearch = () => {
    setShowSearchForm(true);
    setSearchResults([]);
  };

  const handleSelectAll = (event) => {
    if (event.target.checked) {
      setSelectedRows(searchResults.map(row => row.id));
    } else {
      setSelectedRows([]);
    }
  };

  const handleSelectRow = (id) => {
    setSelectedRows(prev => {
      if (prev.includes(id)) {
        return prev.filter(rowId => rowId !== id);
      } else {
        return [...prev, id];
      }
    });
  };

  const handleCloseSnackbar = () => {
    setSnackbar(prev => ({ ...prev, open: false }));
  };

  const handlePrintClick = () => {
    setPrintDialogOpen(true);
  };

  const handlePrintConfirm = async () => {
    try {
      setIsPrinting(true);
      const token = Cookies.get('jwt_token');
      const requestData = {
        ids: selectedRows,
        format: parseInt(selectedFormat)
      };

      const response = await axios.post(
        `${process.env.REACT_APP_API_BASE_URL}/api/voterweb/outputpdf`,
        requestData,
        {
          headers: {
            Authorization: `Bearer ${token}`
          }
        }
      );

      if (response.status === 200) {
        // Make second request to download PDF
        const downloadResponse = await axios.get(
          `${process.env.REACT_APP_API_BASE_URL}/api/download/outputpdf`,
          {
            headers: {
              Authorization: `Bearer ${token}`
            },
            responseType: 'blob' // Important for handling PDF data
          }
        );

        // Create a blob from the PDF data
        const blob = new Blob([downloadResponse.data], { type: 'application/pdf' });
        
        // Create a URL for the blob
        const url = window.URL.createObjectURL(blob);
        
        // Create a temporary link element
        const link = document.createElement('a');
        link.href = url;

        // Format current date and time
        const now = new Date();
        const timestamp = now.getFullYear() +
          ('0' + (now.getMonth() + 1)).slice(-2) +
          ('0' + now.getDate()).slice(-2) + '_' +
          ('0' + now.getHours()).slice(-2) +
          ('0' + now.getMinutes()).slice(-2) +
          ('0' + now.getSeconds()).slice(-2);

        // Create filename with stock code/name and timestamp
        const filename = `${stockInput}_output_${timestamp}.pdf`;
        link.download = filename;
        
        // Append to body, click, and remove
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        
        // Clean up the URL
        window.URL.revokeObjectURL(url);

        setSnackbar({
          open: true,
          message: '下載成功',
          severity: 'success'
        });
      }
    } catch (error) {
      console.error('Error:', error);
      setSnackbar({
        open: true,
        message: '下載失敗，請稍後再試',
        severity: 'error'
      });
    } finally {
      setIsPrinting(false);
      setPrintDialogOpen(false);
    }
  };

  const handlePrintCancel = () => {
    setPrintDialogOpen(false);
  };

  return (
    <div className="vote-print-container">
      {isPrinting && (
        <div className="loading-overlay">
          <CircularProgress />
        </div>
      )}
      <h1>電投表下載</h1>
      
      {showSearchForm ? (
        <div className="search-form">
          <div className="form-group">
            <label htmlFor="year">年份</label>
            <select
              id="year"
              value={selectedYear}
              onChange={(e) => setSelectedYear(e.target.value)}
              className="form-control"
            >
              {years.map(year => (
                <option key={year} value={year}>{year}</option>
              ))}
            </select>
          </div>

          <div className="form-group" ref={idTypeDropdownRef}>
            <label htmlFor="idType">證件</label>
            <div className="custom-select">
              <div 
                className="select-selected"
                onClick={() => setIsIdTypeDropdownOpen(!isIdTypeDropdownOpen)}
              >
                {selectedIdTypes.length > 0 ? getSelectedLabels() : '請選擇證件類型'}
              </div>
              {isIdTypeDropdownOpen && (
                <div className="select-items">
                  {idTypeOptions.map(option => (
                    <div 
                      key={option.value}
                      className="select-item"
                      onClick={() => handleIdTypeChange(option.value)}
                    >
                      <input
                        type="checkbox"
                        checked={selectedIdTypes.includes(option.value)}
                        onChange={() => {}}
                        className="select-checkbox"
                      />
                      <span>{option.label}</span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>

          <div className="form-group stock-input-group">
            <label htmlFor="stock">股號/股名</label>
            <div className="stock-input-container">
              <input
                type="text"
                id="stock"
                value={stockInput}
                onChange={handleStockInputChange}
                className="form-control stock-input"
                placeholder="請輸入股號或股名"
              />
              <select
                value={selectedType}
                onChange={(e) => setSelectedType(e.target.value)}
                className="form-control type-select"
              >
                {typeOptions.map(option => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            </div>
          </div>

          <div className="form-group">
            <label htmlFor="name">戶名</label>
            <input
              type="text"
              id="name"
              value={nameInput}
              onChange={handleNameInputChange}
              className="form-control"
              placeholder="請輸入戶名"
            />
          </div>

          <div className="search-button-container">
            <button 
              className="search-button"
              onClick={handleSearch}
            >
              查詢
            </button>
          </div>
        </div>
      ) : (
        <div className="table-actions">
          <div className="left-actions">
            {selectedRows.length > 0 && (
              <button 
                className="search-button"
                onClick={handlePrintClick}
              >
                下載
              </button>
            )}
          </div>
          <div className="right-actions">
            <button 
              className="search-button"
              onClick={handleNewSearch}
            >
              重新查詢
            </button>
          </div>
        </div>
      )}

      {loading ? (
        <div className="loading-container">
          <CircularProgress />
        </div>
      ) : searchResults.length > 0 ? (
        <>
          <TableContainer component={Paper} className="table-container">
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell padding="checkbox">
                    <Checkbox
                      indeterminate={selectedRows.length > 0 && selectedRows.length < searchResults.length}
                      checked={searchResults.length > 0 && selectedRows.length === searchResults.length}
                      onChange={handleSelectAll}
                    />
                  </TableCell>
                  <TableCell>#</TableCell>
                  <TableCell>股號(股名)</TableCell>
                  <TableCell>姓名</TableCell>
                  <TableCell>身分證字號</TableCell>
                  <TableCell>證件</TableCell>
                  <TableCell>戶號</TableCell>
                  <TableCell>股數</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {searchResults.map((row, index) => (
                  <TableRow key={row.id}>
                    <TableCell padding="checkbox">
                      <Checkbox
                        checked={selectedRows.includes(row.id)}
                        onChange={() => handleSelectRow(row.id)}
                      />
                    </TableCell>
                    <TableCell>{index + 1}</TableCell>
                    <TableCell>{`${row.stock_code}(${row.stock_name})`}</TableCell>
                    <TableCell>{row.phyname}</TableCell>
                    <TableCell>{row.phyid}</TableCell>
                    <TableCell>{row.real_cert_type}</TableCell>
                    <TableCell>{row.account_serial}</TableCell>
                    <TableCell>{row.share_number}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </>
      ) : null}

      {/* Print Format Dialog */}
      <Dialog 
        open={printDialogOpen} 
        onClose={handlePrintCancel}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>格式</DialogTitle>
        <DialogContent>
          <FormControl fullWidth sx={{ mt: 2 }}>
            <InputLabel>選擇格式</InputLabel>
            <Select
              value={selectedFormat}
              label="選擇格式"
              onChange={(e) => setSelectedFormat(e.target.value)}
            >
              {printFormatOptions.map((option) => (
                <MenuItem key={option.value} value={option.value}>
                  {option.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </DialogContent>
        <DialogActions>
          <button 
            className="search-button cancel-button"
            onClick={handlePrintCancel}
          >
            取消
          </button>
          <button 
            className="search-button"
            onClick={handlePrintConfirm}
          >
            確認
          </button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert onClose={handleCloseSnackbar} severity={snackbar.severity} sx={{ width: '100%' }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </div>
  );
};

export default VotePrint; 